/**
 * SelfServiceTab Component
 *
 * Story 9-12: Self-Service Instellingen
 * Configure which profile fields employees can view/edit themselves.
 */

import { useState, useEffect } from 'react';
import { useSelfServiceFields, useUpdateSelfServiceFields } from '../useSettings';
import type { UpdateSelfServiceFieldsRequest } from '@functieloonhuis/shared';

// Define field metadata for display
const FIELD_DEFINITIONS: Record<string, Record<string, { label: string; description: string }>> = {
  employee: {
    name: { label: 'Naam', description: 'Volledige naam van de medewerker' },
    email: { label: 'E-mailadres', description: 'Zakelijk e-mailadres' },
    phone: { label: 'Telefoonnummer', description: 'Zakelijk telefoonnummer' },
    address: { label: 'Adres', description: 'Woonadres van de medewerker' },
    birthDate: { label: 'Geboortedatum', description: 'Geboortedatum' },
    startDate: { label: 'Startdatum', description: 'Datum indiensttreding' },
    department: { label: 'Team', description: 'Team waar medewerker werkt' },
    function: { label: 'Functie', description: 'Huidige functie' },
    manager: { label: 'Leidinggevende', description: 'Directe leidinggevende' },
    salary: { label: 'Salaris', description: 'Bruto maandsalaris' },
    contractType: { label: 'Contracttype', description: 'Vast/tijdelijk contract' },
    hoursPerWeek: { label: 'Uren per week', description: 'Contractuele werkuren' },
  },
  evaluation: {
    selfScore: { label: 'Zelf-score', description: 'Eigen beoordeling scores' },
    managerScore: { label: 'Manager score', description: 'Beoordeling door manager' },
    agreements: { label: 'Afspraken', description: 'Gemaakte afspraken' },
    notes: { label: 'Notities', description: 'Beoordelingsnotities' },
    history: { label: 'Historie', description: 'Vorige beoordelingen' },
  },
};

const ENTITY_LABELS: Record<string, string> = {
  employee: 'Medewerkergegevens',
  evaluation: 'Beoordelingsgegevens',
};

interface FieldState {
  entity: string;
  field: string;
  canView: boolean;
  canEdit: boolean;
}

export function SelfServiceTab() {
  const { data: fields, isLoading, error } = useSelfServiceFields();
  const updateFields = useUpdateSelfServiceFields();

  const [localFields, setLocalFields] = useState<FieldState[]>([]);
  const [isDirty, setIsDirty] = useState(false);

  // Initialize local state from API data
  useEffect(() => {
    if (fields) {
      // Merge API data with all possible fields
      const allFields: FieldState[] = [];

      Object.entries(FIELD_DEFINITIONS).forEach(([entity, entityFields]) => {
        Object.keys(entityFields).forEach((field) => {
          const existing = fields.find((f) => f.entity === entity && f.field === field);
          allFields.push({
            entity,
            field,
            canView: existing?.canView ?? true,
            canEdit: existing?.canEdit ?? false,
          });
        });
      });

      setLocalFields(allFields);
    }
  }, [fields]);

  const handleToggle = (entity: string, field: string, permission: 'canView' | 'canEdit') => {
    setLocalFields((prev) =>
      prev.map((f) => {
        if (f.entity === entity && f.field === field) {
          const updated = { ...f, [permission]: !f[permission] };
          // If canEdit is enabled, canView must also be enabled
          if (permission === 'canEdit' && updated.canEdit) {
            updated.canView = true;
          }
          // If canView is disabled, canEdit must also be disabled
          if (permission === 'canView' && !updated.canView) {
            updated.canEdit = false;
          }
          return updated;
        }
        return f;
      })
    );
    setIsDirty(true);
  };

  const handleSave = async () => {
    const request: UpdateSelfServiceFieldsRequest = {
      fields: localFields.map((f) => ({
        entity: f.entity,
        field: f.field,
        canView: f.canView,
        canEdit: f.canEdit,
      })),
    };

    try {
      await updateFields.mutateAsync(request);
      setIsDirty(false);
    } catch (err) {
      console.error('Failed to update self-service fields:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        Fout bij laden van self-service instellingen: {error.message}
      </div>
    );
  }

  // Group fields by entity
  const groupedFields = localFields.reduce<Record<string, FieldState[]>>((acc, field) => {
    const entity = field.entity;
    if (!acc[entity]) {
      acc[entity] = [];
    }
    acc[entity]!.push(field);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Self-Service Instellingen</h2>
          <p className="mt-1 text-sm text-gray-500">
            Bepaal welke gegevens medewerkers zelf kunnen bekijken en bewerken
          </p>
        </div>
        {isDirty && (
          <button
            onClick={handleSave}
            disabled={updateFields.isPending}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
          >
            {updateFields.isPending ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Opslaan...
              </>
            ) : (
              'Wijzigingen opslaan'
            )}
          </button>
        )}
      </div>

      {/* Legend */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex items-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            <span className="text-gray-600">Bekijken: Medewerker kan dit veld zien</span>
          </div>
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            <span className="text-gray-600">Bewerken: Medewerker kan dit veld aanpassen</span>
          </div>
        </div>
      </div>

      {/* Field Groups */}
      {Object.entries(groupedFields).map(([entity, entityFields]) => (
        <div key={entity} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
            <h3 className="text-sm font-medium text-gray-900">
              {ENTITY_LABELS[entity] || entity}
            </h3>
          </div>
          <div className="divide-y divide-gray-200">
            {entityFields.map((field) => {
              const fieldDef = FIELD_DEFINITIONS[entity]?.[field.field];
              return (
                <div key={`${entity}-${field.field}`} className="px-6 py-4 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {fieldDef?.label || field.field}
                    </p>
                    <p className="text-sm text-gray-500">
                      {fieldDef?.description || ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-6">
                    {/* View Toggle */}
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={field.canView}
                        onChange={() => handleToggle(entity, field.field, 'canView')}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-600">Bekijken</span>
                    </label>

                    {/* Edit Toggle */}
                    <label className={`flex items-center gap-2 ${field.canView ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
                      <input
                        type="checkbox"
                        checked={field.canEdit}
                        onChange={() => handleToggle(entity, field.field, 'canEdit')}
                        disabled={!field.canView}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                      />
                      <span className="text-sm text-gray-600">Bewerken</span>
                    </label>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Info Box */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex gap-3">
          <svg className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div className="text-sm text-yellow-700">
            <p className="font-medium text-yellow-800">Let op</p>
            <p className="mt-1">
              Wijzigingen in deze instellingen zijn direct van toepassing op alle medewerkers.
              Zorg dat gevoelige gegevens zoals salarisgegevens alleen zichtbaar zijn voor de juiste rollen.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
